import { CdkConnectedOverlay, OverlayModule } from '@angular/cdk/overlay';
import { NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  Output,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  CUSTOM_ICON_TYPE,
  InputAutocompleteColors,
  InputAutocompleteItem,
  InputAutocompleteSizes,
} from '@shared/models';
import { IconModule } from '@spartacus/storefront';
import { DropdownMenuComponent, DropdownMenuItemComponent } from '../../v2';
import { SdvorSpinnerComponent } from '../spinner';

@Component({
  selector: 'sd-input-autocomplete',
  templateUrl: './input-autocomplete.component.html',
  styleUrls: ['./input-autocomplete.component.scss'],
  imports: [
    NgIf,
    NgFor,
    NgTemplateOutlet,
    FormsModule,
    IconModule,
    SdvorSpinnerComponent,
    OverlayModule,
    DropdownMenuItemComponent,
    DropdownMenuComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SdvorInputAutocompleteComponent implements AfterViewInit {
  iconTypes = CUSTOM_ICON_TYPE;
  opened = signal(false);

  @ViewChild(CdkConnectedOverlay) overlayDir: CdkConnectedOverlay;
  @ViewChild('autocompleteRef', { read: ElementRef }) autocompleteRef: ElementRef;

  private readonly destroyRef = inject(DestroyRef);

  /** Список элементов*/
  _items: InputAutocompleteItem<unknown>[];

  get items(): InputAutocompleteItem<unknown>[] {
    return this._items;
  }

  @Input() set items(value: InputAutocompleteItem<unknown>[]) {
    if (!value) {
      return;
    }

    this._items = value;
    this.openSuggestMenu(value);
  }

  /** Показывать лоадер */
  @Input() loading: boolean;

  /** Установка введеного в инпут текста */
  @Input() value = '';

  /** Плейсхолдер */
  @Input() placeholder = 'Поиск';

  /** Не активное состояние */
  @Input() disabled = false;

  /** Кастомный шаблон */
  @Input() itemTemplate!: TemplateRef<any>;

  /** Размер */
  @Input() size: InputAutocompleteSizes = InputAutocompleteSizes.L;

  /** Цвет бэкграунда */
  @Input() color: InputAutocompleteColors = InputAutocompleteColors.Gray;

  /** Минимальная длина строки при которой будет производиться поиск */
  @Input() minLength = 0;

  /** Максимальная длина строки при которой будет производиться поиск */
  @Input() maxLength = 255;

  /** Пользовательский ввод */
  @Output() changeInput = new EventEmitter<any>();

  /** Пользователь выбрал элемент или нажал энтер */
  @Output() setInput = new EventEmitter<unknown>();

  @HostBinding('class')
  get classNames(): string[] {
    const baseClass = 'input-autocomplete';

    return [baseClass, `${baseClass}-size-${this.size}`, `${baseClass}-color-${this.color}`];
  }

  ngAfterViewInit(): void {
    this.overlayDir.attach.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const originWidth = this.autocompleteRef.nativeElement.offsetWidth;

      this.overlayDir.overlayRef.updateSize({
        width: originWidth,
      });
    });
  }

  onInputValueChange(): void {
    const value = this.value.trim();

    this.setInput.emit(undefined);
    this.changeInput.emit(value.length >= this.minLength ? this.value : '');
  }

  openSuggestMenu(items: InputAutocompleteItem<unknown>[]): void {
    if (items.length > 0) {
      this.open();
    }
  }

  onInputEnterKeyDown(): void {
    if (!this.items.length) {
      return;
    }

    const item = this.items[0];

    this.value = item.label;
    this.setInput.emit(item.value);
    this.close();
  }

  onDropdownMenuItemSelect(item: InputAutocompleteItem<unknown>): void {
    this.setInput.emit(item.value);
    this.value = item.label;
  }

  close(): void {
    this.opened.set(false);
  }

  open(): void {
    this.opened.set(true);
  }
}
